import React, { useState, useContext } from "react";
import { Container, Row, Col, Form, FormGroup, Button } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import "../styles/Login.css";
import loginImg from "../assets/images/login.png";
import userIcon from "../assets/images/user.png";
import { AuthContext } from "../context/AuthContext";
import { BASE_URL } from "../utils/config";

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { dispatch } = useContext(AuthContext);
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!credentials.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(credentials.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!credentials.password) {
      newErrors.password = "Password is required";
    } else if (credentials.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setCredentials((prevCredentials) => ({
      ...prevCredentials,
      [id]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: "" }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    dispatch({ type: "LOGIN_START" });

    try {
      const res = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: credentials.email.trim(),
          password: credentials.password,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        setError(result.message || "Invalid email or password. Please try again.");
        dispatch({ type: "LOGIN_FAILURE", payload: result.message });
      } else {
        dispatch({ type: "LOGIN_SUCCESS", payload: result });
        setSuccess("Login successful! Redirecting...");
        setTimeout(() => {
          navigate("/");
        }, 1000);
      }
    } catch (error) {
      setError("Network error. Please check your connection and try again.");
      dispatch({ type: "LOGIN_FAILURE", payload: error.message });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <section className="login-section">
      <Container>
        <Row>
          <Col lg="8" className="m-auto">
            <div className="login__container d-flex justify-content-between">
              <div className="login__img">
                <img src={loginImg} alt="Login" />
              </div>

              <div className="login__form">
                <div className="user">
                  <img src={userIcon} alt="User" />
                </div>
                <h2>Welcome Back</h2>
                <p className="login__subtitle">Sign in to your account</p>

                {error && (
                  <div className="alert alert-danger">
                    <i className="ri-error-warning-line"></i> {error}
                  </div>
                )}
                {success && (
                  <div className="alert alert-success">
                    <i className="ri-checkbox-circle-line"></i> {success}
                  </div>
                )}

                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <i className="ri-mail-line input-icon"></i>
                      <input
                        type="email"
                        placeholder="Enter your email"
                        id="email"
                        value={credentials.email}
                        onChange={handleChange}
                        className={errors.email ? "error" : ""}
                        autoComplete="email"
                      />
                    </div>
                    {errors.email && (
                      <span className="error-message">
                        <i className="ri-error-warning-line"></i> {errors.email}
                      </span>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="password">Password</label>
                    <div className="password__input">
                      <i className="ri-lock-line input-icon"></i>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        id="password"
                        value={credentials.password}
                        onChange={handleChange}
                        className={errors.password ? "error" : ""}
                        autoComplete="current-password"
                      />
                      <i
                        className={`password-toggle ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}
                        onClick={togglePasswordVisibility}
                      ></i>
                    </div>
                    {errors.password && (
                      <span className="error-message">
                        <i className="ri-error-warning-line"></i> {errors.password}
                      </span>
                    )}
                  </FormGroup>

                  <Button
                    className="btn secondary__btn auth__btn"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span> Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </Button>
                </Form>

                <div className="login__footer">
                  <p>
                    Don't have an account?{" "}
                    <Link to="/register" className="register-link">
                      Create Account
                    </Link>
                  </p>
                  <p className="admin-link">
                    <Link to="/admin/login">Admin Login</Link>
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Login;
